import NumberFlow from '@number-flow/react';
import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { ArrowDownRightIcon, ArrowUpRightIcon, DashIcon } from '../icons';
import { palettes, space, tokens } from '../theme/tokens';
import { IconWrapper } from './Icon';
import { Tooltip } from './Tooltip';

type Direction = 'up' | 'down' | 'neutral';

const tone = {
	up: {
		background: tokens.success.background,
		border: tokens.success.border,
		icon: tokens.success.iconOnBg,
	},
	down: {
		background: tokens.error.background,
		border: tokens.error.border,
		icon: tokens.error.iconOnBg,
	},
	neutral: {
		background: palettes.heatherGray['50'],
		border: tokens.border.default,
		icon: tokens.icon.primary,
	},
} as const;

const directionIcon = {
	up: ArrowUpRightIcon,
	down: ArrowDownRightIcon,
	neutral: DashIcon,
} as const;

const Container = styled.div<{ $direction: Direction; $animate?: boolean }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: ${space['3xs']}px;
	padding: ${space['2xs']}px 6px;
	border-radius: 6px;
	box-sizing: border-box;
	background-color: ${(props) => tone[props.$direction].background};
	border: 1px solid ${(props) => tone[props.$direction].border};
	${(props) =>
		props.$animate && 'transition: background-color 600ms ease, border-color 600ms ease;'}
`;

const Value = styled.span`
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: normal;
	white-space: nowrap;
`;

const Meta = styled.span`
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.regular};
	line-height: normal;
	white-space: nowrap;
`;

type DeltaUnit = 'percentage' | 'numeric' | 'percentagePoint';

const UNIT_SUFFIX: Record<DeltaUnit, string> = {
	percentage: '%',
	numeric: '',
	percentagePoint: 'pp',
};

export function Delta({
	currentValue,
	previousValue,
	label,
	unit = 'percentage',
	style,
	animate = false,
	tooltip,
	tooltipPortal = false,
}: {
	currentValue: number;
	previousValue: number;
	label?: string;
	unit?: DeltaUnit;
	style?: CSSProperties;
	animate?: boolean;
	tooltip?: ReactNode;
	tooltipPortal?: boolean;
}) {
	const delta =
		unit === 'percentagePoint'
			? Math.round(currentValue) - Math.round(previousValue)
			: currentValue - previousValue;
	const deltaPercentage =
		previousValue > 0 ? (delta / previousValue) * 100 : currentValue > 0 ? 100 : 0;
	const displayValue = unit === 'percentage' ? deltaPercentage : delta;
	// Round first so a tiny delta (e.g. -0.4) can't render as "-0" with a down arrow.
	const roundedValue = Math.round(displayValue) || 0;

	const direction: Direction = roundedValue > 0 ? 'up' : roundedValue < 0 ? 'down' : 'neutral';

	const Icon = directionIcon[direction];

	const badge = (
		<Container $direction={direction} $animate={animate} style={style}>
			<IconWrapper size={16} color={tone[direction].icon}>
				<Icon />
			</IconWrapper>
			{direction !== 'neutral' && (
				<Value>
					{direction === 'up' && '+'}
					{animate ? (
						<NumberFlow
							value={roundedValue}
							format={{ maximumFractionDigits: 0 }}
							locales="en-US"
							style={{ display: 'inline-block' }}
						/>
					) : (
						roundedValue
					)}
					{UNIT_SUFFIX[unit]}
				</Value>
			)}
			{label && <Meta>{label}</Meta>}
		</Container>
	);

	if (!tooltip) {
		return badge;
	}

	return (
		<Tooltip content={tooltip} fitContent portal={tooltipPortal}>
			{badge}
		</Tooltip>
	);
}
